import { formatChips } from '@polf/shared/locale'

import {
  BeatTheProEntryStateEnum,
  type BtpPlayerFragment,
} from '../../__generated__/hooks'
import { IconChevronLeft } from '../../components/Icon'
import Avatar from '../Score/components/Avatar'

/**
 * One player in the pro's betting console. Shows name · flight · bet state
 * with a status chip. Tapping dispatches to place-a-bet (no entry) or
 * decide-the-bet (has entry). Read-only → not tappable (spectator).
 */

const chip = (label: string, cls: string) => (
  <span
    className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${cls}`}
  >
    {label}
  </span>
)

const statusChip = (player: BtpPlayerFragment) => {
  const extra = player.entry?.beatTheProExtra
  if (!extra) return null
  if (extra.allIn && extra.state === BeatTheProEntryStateEnum.Undecided) {
    return chip('All-in', 'bg-flag/15 text-flag uppercase')
  }
  switch (extra.state) {
    case BeatTheProEntryStateEnum.Won:
      return chip('Seier', 'bg-green-100 text-green-700')
    case BeatTheProEntryStateEnum.Lost:
      return chip('Tap', 'bg-flag/15 text-flag')
    default:
      return chip('Venter', 'bg-ink-100 text-ink-500')
  }
}

const betLine = (player: BtpPlayerFragment) => {
  const extra = player.entry?.beatTheProExtra
  const flightPart = player.flight
    ? `Flight ${player.flight.flightNumber}`
    : null
  if (!extra) return [flightPart, 'Ingen bet'].filter(Boolean).join(' · ')

  const stake = extra.allIn ? 'All-in' : `Bet: ${formatChips(extra.bet)}`
  let payout: string | null = null
  if (extra.state === BeatTheProEntryStateEnum.Won) {
    payout = `+${formatChips(player.entry?.value ?? 0)}`
  } else if (extra.state === BeatTheProEntryStateEnum.Lost) {
    payout = formatChips(player.entry?.value ?? 0)
  }
  return [flightPart, stake, payout].filter(Boolean).join(' · ')
}

const BtpPlayerRow = ({
  player,
  readOnly,
  onTap,
}: {
  player: BtpPlayerFragment
  readOnly: boolean
  onTap: () => void
}) => {
  const won =
    player.entry?.beatTheProExtra?.state === BeatTheProEntryStateEnum.Won
  const inner = (
    <div className="flex items-center gap-3">
      <Avatar name={player.name} isYou={false} />
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2 min-w-0">
          <span className="flex-1 text-sm font-semibold text-ink-900 truncate">
            {player.name}
          </span>
          {statusChip(player)}
        </span>
        <span
          className={`block text-[11px] mt-0.5 font-mono num ${
            won ? 'text-gold' : 'text-ink-500'
          }`}
        >
          {betLine(player)}
        </span>
      </span>
      {!readOnly && (
        <IconChevronLeft className="w-4 h-4 rotate-180 text-ink-300 shrink-0" />
      )}
    </div>
  )

  if (readOnly) {
    return <div className="bg-paper rounded-lg p-3 shadow-sm">{inner}</div>
  }
  return (
    <button
      type="button"
      onClick={onTap}
      className="w-full text-left bg-paper rounded-lg p-3 shadow-sm active:bg-cream-card transition-colors"
    >
      {inner}
    </button>
  )
}

export default BtpPlayerRow
